import { fontOptions } from "../data";
import { useRef, useState, useCallback, useEffect } from "react";
import ReactCrop, { type Crop, type PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { getCroppedImg } from '../../../utils/cropImage';
import { extractColors } from '../../../utils/colorExtractor';
import apiService from '../../../services/api';
import FieldInfo from "../../../components/base/FieldInfo";
import LogoBackgroundRemovalModal from "../../../components/LogoBackgroundRemovalModal";
const BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface StepBusinessPortfolioProps {
  errors?: Record<string, string>;
  userId: string;
  businessType: "service" | "product" | "both" | null;
  products: { name: string; url: string; priority: boolean }[];
  services: { name: string; url: string; priority: boolean }[];
  differentiators: string[];
  socialPlatforms: { platform: string; url: string }[];
  brandLogo: string | null;
  primaryFont: string;
  secondaryFont: string;
  homePageUrl: string;
  productPageUrl: string;
  servicePageUrl: string;
  contactPageUrl: string;
  aboutPageUrl: string;
  blogPageUrl: string;
  customPageUrls: { name: string; url: string }[];
  primaryColor: string;
  secondaryColor: string;
  showPrimaryFontDropdown: boolean;
  showSecondaryFontDropdown: boolean;
  onAddProduct: () => void;
  onUpdateProduct: (
    index: number,
    field: "name" | "url",
    value: string,
  ) => void;
  onRemoveProduct: (index: number) => void;
  onToggleProductPriority: (index: number) => void;
  onAddService: () => void;
  onUpdateService: (
    index: number,
    field: "name" | "url",
    value: string,
  ) => void;
  onRemoveService: (index: number) => void;
  onToggleServicePriority: (index: number) => void;
  onAddDifferentiator: () => void;
  onUpdateDifferentiator: (index: number, value: string) => void;
  onUpdateSocialPlatform: (index: number, value: string) => void;
  onLogoUpload: (logo: string) => void;
  onPrimaryFontChange: (value: string) => void;
  onSecondaryFontChange: (value: string) => void;
  onShowPrimaryFontDropdown: (value: boolean) => void;
  onShowSecondaryFontDropdown: (value: boolean) => void;
  onHomePageUrlChange: (value: string) => void;
  onProductPageUrlChange: (value: string) => void;
  onServicePageUrlChange: (value: string) => void;
  onContactPageUrlChange: (value: string) => void;
  onAboutPageUrlChange: (value: string) => void;
  onBlogPageUrlChange: (value: string) => void;
  onAddCustomPageUrl: () => void;
  onUpdateCustomPageUrl: (
    index: number,
    field: "name" | "url",
    value: string,
  ) => void;
  onRemoveCustomPageUrl: (index: number) => void;
  onPrimaryColorChange: (value: string) => void;
  onSecondaryColorChange: (value: string) => void;
}

export default function StepBusinessPortfolio(
  props: StepBusinessPortfolioProps,
) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  // The logo uploaded in this session — sent to "Remove background".
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [showBgRemoval, setShowBgRemoval] = useState(false);
  // The background-removed version, saved next to the original.
  const [transparentLogoPreview, setTransparentLogoPreview] = useState<string | null>(null);

  // Logo Editor State
  const [logoError, setLogoError] = useState<string | null>(null);
  const [logoSuccess, setLogoSuccess] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editorImage, setEditorImage] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();

  // Preview Modal State
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Font Selection State
  const [primaryFontSearch, setPrimaryFontSearch] = useState("");
  const [secondaryFontSearch, setSecondaryFontSearch] = useState("");
  const [primaryFontIsCustom, setPrimaryFontIsCustom] = useState(false);
  const [secondaryFontIsCustom, setSecondaryFontIsCustom] = useState(false);

  useEffect(() => {
    // Dynamically load the fonts from Google Fonts so they render correctly in the dropdown
    const link = document.createElement("link");
    const families = fontOptions.map(f => `family=${f.replace(/ /g, "+")}`).join("&");
    link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
    link.rel = "stylesheet";
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  useEffect(() => {
    if (props.errors?.portfolio) {
      document.getElementById("portfolio-error")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [props.errors?.portfolio]);

  const handleLogoUpload = async (file: File) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      setEditorImage(reader.result as string);
      setShowEditor(true);
      setCrop(undefined);
      setCompletedCrop(undefined);
    });
    reader.readAsDataURL(file);
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setCrop({
      unit: '%',
      width: 90,
      height: 90,
      x: 5,
      y: 5
    });
  };

  const handleSaveCrop = async () => {
    if (!imgRef.current || !completedCrop) return;

    try {
      setLogoError(null);
      setLogoSuccess(false);
      setUploading(true);
      setShowEditor(false);

      const croppedImageBlob = await getCroppedImg(
        imgRef.current,
        completedCrop
      );

      if (!croppedImageBlob) throw new Error("Cropping failed");

      const previewUrl = URL.createObjectURL(croppedImageBlob);
      setLogoPreview(previewUrl);

      // Extract colors
      const img = new Image();
      img.src = previewUrl;
      img.onload = () => {
        try {
          const colors = extractColors(img);
          if (colors && colors.length > 0) {
            props.onPrimaryColorChange(colors[0]);
            if (colors.length > 1) {
              props.onSecondaryColorChange(colors[1]);
            }
          }
        } catch (e) {
          console.error("Color extraction failed", e);
        }
      };

      const croppedFile = new File([croppedImageBlob], "logo.png", {
        type: "image/png",
      });

      if (!props.userId) {
        alert("Session lost. Please restart from Step 1 to generate a valid User ID.");
        throw new Error("Missing user_id");
      }

      const response = await apiService.uploadBrandLogo(
        props.userId,
        croppedFile
      );
      
      const logoUrl = response.data?.logo_url;

      if (logoUrl) {
        props.onLogoUpload(logoUrl);
        setLogoFile(croppedFile);
        setTransparentLogoPreview(null); // a new original clears the old transparent version
        setLogoSuccess(true);
        setTimeout(() => setLogoSuccess(false), 5000);
      }
      setUploading(false);
    } catch (error) {
      setUploading(false);
      setLogoError(error instanceof Error ? error.message : "Logo upload failed. Please try again.");
      console.error("Logo upload failed", error);
    }
  };

  // "Use transparent logo": saved next to the original — each post chooses which one to place.
  const handleUseTransparentLogo = async (file: File, previewUrl: string) => {
    if (!props.userId) throw new Error("Session lost. Please restart from Step 1.");
    const response = await apiService.uploadBrandLogoTransparent(props.userId, file);
    if (!response.data?.logo_transparent_url) throw new Error("Saving the transparent logo failed. Please try again.");
    setTransparentLogoPreview(previewUrl);
    setShowBgRemoval(false);
    setLogoError(null);
    setLogoSuccess(true);
    setTimeout(() => setLogoSuccess(false), 5000);
  };
  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">
          Business Portfolio
        </h2>
        {props.errors?.portfolio && (
          <div id="portfolio-error" className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600 text-sm">
            <i className="ri-error-warning-line"></i>
            {props.errors.portfolio}
          </div>
        )}
      </div>

      <div className="border-t border-gray-100"></div>

      {/* Products Block */}
      {(props.businessType === "product" || props.businessType === "both") && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-teal-500/10 flex items-center justify-center">
              <i className="ri-shopping-bag-3-line text-teal-600 text-sm"></i>
            </div>
            Products
          </h3>

          <div className="space-y-3">
            {props.products.map((product, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-lg p-4 space-y-3"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={product.name}
                      onChange={(e) =>
                        props.onUpdateProduct(index, "name", e.target.value.slice(0, 255))
                      }
                      maxLength={255}
                      placeholder={`Product name ${index + 1}`}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                    {product.name.length >= 255 && (
                      <p className="mt-1 text-xs text-orange-500">Maximum length of 255 characters reached.</p>
                    )}
                  </div>
                  {props.products.length > 1 && (
                    <button
                      type="button"
                      onClick={() => props.onRemoveProduct(index)}
                      className="text-gray-400 hover:text-red-500 transition-colors cursor-pointer self-start mt-2"
                    >
                      <i className="ri-close-line text-lg"></i>
                    </button>
                  )}
                </div>
                <div>
                  <input
                    type="url"
                    value={product.url}
                    onChange={(e) =>
                      props.onUpdateProduct(index, "url", e.target.value.slice(0, 500))
                    }
                    maxLength={500}
                    placeholder="https://example.com/product-page (optional)"
                    className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                      props.errors?.[`products_${index}_url`] ? 'border-red-400 bg-red-50' : 'border-gray-200'
                    }`}
                  />
                  {props.errors?.[`products_${index}_url`] ? (
                    <p className="mt-1 text-xs text-red-500">{props.errors[`products_${index}_url`]}</p>
                  ) : product.url.length >= 500 ? (
                    <p className="mt-1 text-xs text-orange-500">Maximum length of 500 characters reached.</p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => props.onToggleProductPriority(index)}
                  className={`flex items-center gap-2 text-sm font-medium cursor-pointer transition-colors ${product.priority
                    ? "text-teal-600"
                    : "text-gray-400 hover:text-gray-600"
                    }`}
                >
                  <div
                    className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${product.priority
                      ? "border-teal-500 bg-teal-500"
                      : "border-gray-300"
                      }`}
                  >
                    {product.priority && (
                      <i className="ri-check-line text-white text-xs"></i>
                    )}
                  </div>
                  Mark as Priority
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={props.onAddProduct}
            className="mt-3 text-sm text-teal-600 font-medium hover:text-teal-700 transition-colors cursor-pointer flex items-center gap-1"
          >
            <i className="ri-add-line"></i>
            Add Product
          </button>
        </div>
      )}

      {(props.businessType === "product" || props.businessType === "both") && (
        <div className="border-t border-gray-100"></div>
      )}

      {/* Services Block */}
      {(props.businessType === "service" || props.businessType === "both") && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-teal-500/10 flex items-center justify-center">
              <i className="ri-service-line text-teal-600 text-sm"></i>
            </div>
            Services
          </h3>

          <div className="space-y-3">
            {props.services.map((service, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-lg p-4 space-y-3"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={service.name}
                      onChange={(e) =>
                        props.onUpdateService(index, "name", e.target.value.slice(0, 255))
                      }
                      maxLength={255}
                      placeholder={`Service name ${index + 1}`}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                    {service.name.length >= 255 && (
                      <p className="mt-1 text-xs text-orange-500">Maximum length of 255 characters reached.</p>
                    )}
                  </div>
                  {props.services.length > 1 && (
                    <button
                      type="button"
                      onClick={() => props.onRemoveService(index)}
                      className="text-gray-400 hover:text-red-500 transition-colors cursor-pointer self-start mt-2"
                    >
                      <i className="ri-close-line text-lg"></i>
                    </button>
                  )}
                </div>
                <div>
                  <input
                    type="url"
                    value={service.url}
                    onChange={(e) =>
                      props.onUpdateService(index, "url", e.target.value.slice(0, 500))
                    }
                    maxLength={500}
                    placeholder="https://example.com/service-page (optional)"
                    className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                      props.errors?.[`services_${index}_url`] ? 'border-red-400 bg-red-50' : 'border-gray-200'
                    }`}
                  />
                  {props.errors?.[`services_${index}_url`] ? (
                    <p className="mt-1 text-xs text-red-500">{props.errors[`services_${index}_url`]}</p>
                  ) : service.url.length >= 500 ? (
                    <p className="mt-1 text-xs text-orange-500">Maximum length of 500 characters reached.</p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => props.onToggleServicePriority(index)}
                  className={`flex items-center gap-2 text-sm font-medium cursor-pointer transition-colors ${service.priority
                    ? "text-teal-600"
                    : "text-gray-400 hover:text-gray-600"
                    }`}
                >
                  <div
                    className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${service.priority
                      ? "border-teal-500 bg-teal-500"
                      : "border-gray-300"
                      }`}
                  >
                    {service.priority && (
                      <i className="ri-check-line text-white text-xs"></i>
                    )}
                  </div>
                  Mark as Priority
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={props.onAddService}
            className="mt-3 text-sm text-teal-600 font-medium hover:text-teal-700 transition-colors cursor-pointer flex items-center gap-1"
          >
            <i className="ri-add-line"></i>
            Add Service
          </button>
        </div>
      )}

      {(props.businessType === "service" || props.businessType === "both") && (
        <div className="border-t border-gray-100"></div>
      )}

      {/* Page Structure / Key URLs */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-teal-500/10 flex items-center justify-center">
            <i className="ri-links-line text-teal-600 text-sm"></i>
          </div>
          Page Structure / Key URLs
        </h3>

        <div className="space-y-4">
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
              <span>Home Page URL</span>
              <FieldInfo fieldKey="homePageUrl" />
            </label>
            <input
              type="url"
              value={props.homePageUrl}
              onChange={(e) => props.onHomePageUrlChange(e.target.value)}
              placeholder="https://example.com/"
              className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                props.errors?.homePageUrl ? 'border-red-400 bg-red-50' : 'border-gray-200'
              }`}
            />
            {props.errors?.homePageUrl && (
              <p className="mt-1 text-xs text-red-500">{props.errors.homePageUrl}</p>
            )}
          </div>
          {(props.businessType === "product" || props.businessType === "both") && (
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                <span>Product Listing Page URL</span>
                <FieldInfo fieldKey="productPageUrl" />
              </label>
              <input
                type="url"
                value={props.productPageUrl}
                onChange={(e) => props.onProductPageUrlChange(e.target.value)}
                placeholder="https://example.com/products"
                className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                  props.errors?.productPageUrl ? 'border-red-400 bg-red-50' : 'border-gray-200'
                }`}
              />
              {props.errors?.productPageUrl && (
                <p className="mt-1 text-xs text-red-500">{props.errors.productPageUrl}</p>
              )}
            </div>
          )}
          {(props.businessType === "service" || props.businessType === "both") && (
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                <span>Service Listing Page URL</span>
                <FieldInfo fieldKey="servicePageUrl" />
              </label>
              <input
                type="url"
                value={props.servicePageUrl}
                onChange={(e) => props.onServicePageUrlChange(e.target.value)}
                placeholder="https://example.com/services"
                className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                  props.errors?.servicePageUrl ? 'border-red-400 bg-red-50' : 'border-gray-200'
                }`}
              />
              {props.errors?.servicePageUrl && (
                <p className="mt-1 text-xs text-red-500">{props.errors.servicePageUrl}</p>
              )}
            </div>
          )}
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
              <span>Contact Page URL</span>
              <FieldInfo fieldKey="contactPageUrl" />
            </label>
            <input
              type="url"
              value={props.contactPageUrl}
              onChange={(e) => props.onContactPageUrlChange(e.target.value)}
              placeholder="https://example.com/contact"
              className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                props.errors?.contactPageUrl ? 'border-red-400 bg-red-50' : 'border-gray-200'
              }`}
            />
            {props.errors?.contactPageUrl && (
              <p className="mt-1 text-xs text-red-500">{props.errors.contactPageUrl}</p>
            )}
          </div>
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
              <span>About Page URL</span>
              <FieldInfo fieldKey="aboutPageUrl" />
            </label>
            <input
              type="url"
              value={props.aboutPageUrl}
              onChange={(e) => props.onAboutPageUrlChange(e.target.value)}
              placeholder="https://example.com/about"
              className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                props.errors?.aboutPageUrl ? 'border-red-400 bg-red-50' : 'border-gray-200'
              }`}
            />
            {props.errors?.aboutPageUrl && (
              <p className="mt-1 text-xs text-red-500">{props.errors.aboutPageUrl}</p>
            )}
          </div>
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
              <span>Blog Page URL</span>
              <FieldInfo fieldKey="blogPageUrl" />
            </label>
            <input
              type="url"
              value={props.blogPageUrl}
              onChange={(e) => props.onBlogPageUrlChange(e.target.value)}
              placeholder="https://example.com/blog"
              className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                props.errors?.blogPageUrl ? 'border-red-400 bg-red-50' : 'border-gray-200'
              }`}
            />
            {props.errors?.blogPageUrl && (
              <p className="mt-1 text-xs text-red-500">{props.errors.blogPageUrl}</p>
            )}
          </div>

          {/* Custom Page URLs */}
          {props.customPageUrls.map((page, index) => (
            <div key={index}>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm text-gray-700">
                  <span className="font-medium">
                    {page.name.trim() || "Custom Page Name"}
                  </span>
                </label>
                {props.customPageUrls.length > 1 && (
                  <button
                    type="button"
                    onClick={() => props.onRemoveCustomPageUrl(index)}
                    className="text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-lg"></i>
                  </button>
                )}
              </div>
              <div className="space-y-2">
                <input
                  type="text"
                  value={page.name}
                  onChange={(e) =>
                    props.onUpdateCustomPageUrl(index, "name", e.target.value)
                  }
                  placeholder="Page name (e.g. FAQ, Testimonials)"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                <div>
                  <input
                    type="url"
                    value={page.url}
                    onChange={(e) =>
                      props.onUpdateCustomPageUrl(index, "url", e.target.value)
                    }
                    placeholder="https://example.com/custom-page"
                    className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                      props.errors?.[`customPageUrls_${index}_url`] ? 'border-red-400 bg-red-50' : 'border-gray-200'
                    }`}
                  />
                  {props.errors?.[`customPageUrls_${index}_url`] && (
                    <p className="mt-1 text-xs text-red-500">{props.errors[`customPageUrls_${index}_url`]}</p>
                  )}
                </div>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={props.onAddCustomPageUrl}
            className="text-sm text-teal-600 font-medium hover:text-teal-700 transition-colors cursor-pointer flex items-center gap-1"
          >
            <i className="ri-add-line"></i>
            Add Custom Page URL
          </button>
        </div>
      </div>

      <div className="border-t border-gray-100"></div>

      {/* Brand Differentiators */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-teal-500/10 flex items-center justify-center">
            <i className="ri-star-line text-teal-600 text-sm"></i>
          </div>
          Brand Differentiators
        </h3>

        <div className="space-y-3">
          {props.differentiators.map((diff, index) => (
            <div key={index} className="flex flex-col">
              <input
                type="text"
                value={diff}
                onChange={(e) =>
                  props.onUpdateDifferentiator(index, e.target.value.slice(0, 500))
                }
                maxLength={500}
                placeholder={`What makes your brand unique? ${index + 1}`}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              {diff.length >= 500 && (
                <p className="mt-1 text-xs text-orange-500">Maximum length of 500 characters reached.</p>
              )}
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={props.onAddDifferentiator}
          className="mt-3 text-sm text-teal-600 font-medium hover:text-teal-700 transition-colors cursor-pointer flex items-center gap-1"
        >
          <i className="ri-add-line"></i>
          Add Differentiator
        </button>
      </div>

      <div className="border-t border-gray-100"></div>

      {/* Optional: Brand Identity */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center">
              <i className="ri-palette-line text-gray-600 text-sm"></i>
            </div>
            <span>Brand Identity</span>
            <FieldInfo fieldKey="brandIdentity" />
          </h3>
        </div>
        {/* <p className="text-xs text-gray-400 mb-4 ml-9">
          Non-blocking — can be added later in your profile
        </p> */}

        <div className="space-y-5">
          {/* Logo */}
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
              <span>Brand Logo</span>
              <FieldInfo fieldKey="brandLogo" />
            </label>
            <div className="flex items-center gap-6">
              <div
                className="w-20 h-20 border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center bg-gray-50 overflow-hidden cursor-pointer hover:border-teal-300 transition-colors relative group"
                onClick={() =>
                  (logoPreview || props.brandLogo) && setShowPreviewModal(true)
                }
              >
                {logoPreview || props.brandLogo ? (
                  <>
                    <img
                      src={logoPreview || props.brandLogo || ""}
                      alt="Brand Logo"
                      className="w-full h-full object-contain p-1"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                      <i className="ri-eye-line text-white text-lg"></i>
                    </div>
                  </>
                ) : (
                  <>
                    <i className="ri-image-line text-2xl text-gray-300"></i>
                    <span className="text-xs text-gray-400 mt-1">No logo</span>
                  </>
                )}
              </div>
              {transparentLogoPreview && (
                <div className="text-center -ml-3">
                  <div
                    className="w-20 h-20 border border-gray-200 rounded-lg overflow-hidden"
                    style={{ background: "repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%) 0 0 / 12px 12px" }}
                  >
                    <img src={transparentLogoPreview} alt="Transparent brand logo" className="w-full h-full object-contain p-1" />
                  </div>
                  <p className="mt-1 text-[11px] font-medium text-gray-500">Transparent</p>
                </div>
              )}
              <div>
                <button
                  type="button"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${uploading
                    ? "bg-teal-400 cursor-not-allowed"
                    : "bg-teal-500 hover:bg-teal-600 cursor-pointer"
                    } text-white`}
                >
                  {uploading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <i className="ri-upload-cloud-line"></i>
                      Upload Logo
                    </>
                  )}
                </button>
                {logoFile && logoPreview && !uploading && (
                  <button
                    type="button"
                    onClick={() => setShowBgRemoval(true)}
                    className="mt-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2 border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 cursor-pointer"
                  >
                    <i className="ri-eraser-line"></i>
                    Remove background
                  </button>
                )}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;

                    handleLogoUpload(file);
                  }}
                />
                {/* <p className="text-xs text-gray-400 mt-2">
                  PNG or SVG, transparent background, min 200x200px
                </p> */}
                {logoError && (
                  <p className="text-xs text-red-500 mt-2 font-medium">
                    <i className="ri-error-warning-line mr-1"></i>
                    {logoError}
                  </p>
                )}
                {logoSuccess && (
                  <p className="text-xs text-green-600 mt-2 font-medium">
                    <i className="ri-checkbox-circle-line mr-1"></i>
                    Logo uploaded successfully!
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Fonts */}
          <div className="grid grid-cols-2 gap-4">
            {/* Primary Font */}
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
                <span>Primary Font <span className="text-gray-400 font-normal ml-0.5">(Headings)</span></span>
                <FieldInfo fieldKey="primaryFont" />
              </label>
              <div className="relative">
                <div className="flex items-center border border-gray-200 rounded-lg bg-white overflow-hidden focus-within:ring-2 focus-within:ring-teal-500 focus-within:border-transparent">
                  <input
                    type="text"
                    value={primaryFontSearch}
                    onChange={(e) => {
                      setPrimaryFontSearch(e.target.value);
                      props.onShowPrimaryFontDropdown(true);
                    }}
                    onFocus={() => {
                      setPrimaryFontSearch(props.primaryFont && !primaryFontIsCustom ? props.primaryFont : "");
                      props.onShowPrimaryFontDropdown(true);
                    }}
                    placeholder={props.primaryFont || "Search fonts..."}
                    className="w-full px-4 py-2.5 text-sm focus:outline-none"
                    style={{
                      fontFamily: props.primaryFont && !props.showPrimaryFontDropdown && !primaryFontIsCustom 
                        ? `"${props.primaryFont}", sans-serif` 
                        : "inherit"
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => props.onShowPrimaryFontDropdown(!props.showPrimaryFontDropdown)}
                    className="px-3 text-gray-400 hover:text-gray-600"
                  >
                    <i className={`ri-arrow-down-s-line transition-transform ${props.showPrimaryFontDropdown ? "rotate-180" : ""}`}></i>
                  </button>
                </div>
                {props.showPrimaryFontDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                    {fontOptions
                      .filter((font) => font.toLowerCase().includes(primaryFontSearch.toLowerCase()))
                      .map((font) => (
                        <button
                          key={font}
                          type="button"
                          onClick={() => {
                            props.onPrimaryFontChange(font);
                            setPrimaryFontSearch(font);
                            setPrimaryFontIsCustom(false);
                            props.onShowPrimaryFontDropdown(false);
                          }}
                          className="w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 cursor-pointer"
                          style={{ fontFamily: `"${font}", sans-serif` }}
                        >
                          {font}
                        </button>
                      ))}
                    <button
                      type="button"
                      onClick={() => {
                        setPrimaryFontIsCustom(true);
                        props.onPrimaryFontChange("");
                        setPrimaryFontSearch("");
                        props.onShowPrimaryFontDropdown(false);
                      }}
                      className="w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 cursor-pointer font-medium text-teal-600 border-t border-gray-100"
                    >
                      Other (Custom Font)
                    </button>
                  </div>
                )}
              </div>
              {primaryFontIsCustom && (
                <div className="mt-3">
                  <input
                    type="text"
                    value={props.primaryFont}
                    onChange={(e) => props.onPrimaryFontChange(e.target.value)}
                    placeholder="Enter custom font name"
                    className="w-full px-4 py-2.5 border border-teal-200 bg-teal-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              )}
            </div>

            {/* Secondary Font */}
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
                <span>Secondary Font <span className="text-gray-400 font-normal ml-0.5">(Body)</span></span>
                <FieldInfo fieldKey="secondaryFont" />
              </label>
              <div className="relative">
                <div className="flex items-center border border-gray-200 rounded-lg bg-white overflow-hidden focus-within:ring-2 focus-within:ring-teal-500 focus-within:border-transparent">
                  <input
                    type="text"
                    value={secondaryFontSearch}
                    onChange={(e) => {
                      setSecondaryFontSearch(e.target.value);
                      props.onShowSecondaryFontDropdown(true);
                    }}
                    onFocus={() => {
                      setSecondaryFontSearch(props.secondaryFont && !secondaryFontIsCustom ? props.secondaryFont : "");
                      props.onShowSecondaryFontDropdown(true);
                    }}
                    placeholder={props.secondaryFont || "Search fonts..."}
                    className="w-full px-4 py-2.5 text-sm focus:outline-none"
                    style={{
                      fontFamily: props.secondaryFont && !props.showSecondaryFontDropdown && !secondaryFontIsCustom 
                        ? `"${props.secondaryFont}", sans-serif` 
                        : "inherit"
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => props.onShowSecondaryFontDropdown(!props.showSecondaryFontDropdown)}
                    className="px-3 text-gray-400 hover:text-gray-600"
                  >
                    <i className={`ri-arrow-down-s-line transition-transform ${props.showSecondaryFontDropdown ? "rotate-180" : ""}`}></i>
                  </button>
                </div>
                {props.showSecondaryFontDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                    {fontOptions
                      .filter((font) => font.toLowerCase().includes(secondaryFontSearch.toLowerCase()))
                      .map((font) => (
                        <button
                          key={font}
                          type="button"
                          onClick={() => {
                            props.onSecondaryFontChange(font);
                            setSecondaryFontSearch(font);
                            setSecondaryFontIsCustom(false);
                            props.onShowSecondaryFontDropdown(false);
                          }}
                          className="w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 cursor-pointer"
                          style={{ fontFamily: `"${font}", sans-serif` }}
                        >
                          {font}
                        </button>
                      ))}
                    <button
                      type="button"
                      onClick={() => {
                        setSecondaryFontIsCustom(true);
                        props.onSecondaryFontChange("");
                        setSecondaryFontSearch("");
                        props.onShowSecondaryFontDropdown(false);
                      }}
                      className="w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 cursor-pointer font-medium text-teal-600 border-t border-gray-100"
                    >
                      Other (Custom Font)
                    </button>
                  </div>
                )}
              </div>
              {secondaryFontIsCustom && (
                <div className="mt-3">
                  <input
                    type="text"
                    value={props.secondaryFont}
                    onChange={(e) => props.onSecondaryFontChange(e.target.value)}
                    placeholder="Enter custom font name"
                    className="w-full px-4 py-2.5 border border-teal-200 bg-teal-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Colors */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
                <span>Primary Color</span>
                <FieldInfo fieldKey="primaryColor" />
              </label>

              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={props.primaryColor}
                  onChange={(e) => props.onPrimaryColorChange(e.target.value)}
                  className="w-10 h-10 p-0 border-0 bg-transparent cursor-pointer"
                />

                <input
                  type="text"
                  value={props.primaryColor}
                  onChange={(e) => props.onPrimaryColorChange(e.target.value)}
                  placeholder="#54B8A6"
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
                <span>Secondary Color</span>
                <FieldInfo fieldKey="secondaryColor" />
              </label>

              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={props.secondaryColor}
                  onChange={(e) => props.onSecondaryColorChange(e.target.value)}
                  className="w-10 h-10 p-0 border-0 bg-transparent cursor-pointer"
                />

                <input
                  type="text"
                  value={props.secondaryColor}
                  onChange={(e) => props.onSecondaryColorChange(e.target.value)}
                  placeholder="#8D94BB"
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-100"></div>

      {/* Optional: Existing Digital Assets */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center">
              <i className="ri-global-line text-gray-600 text-sm"></i>
            </div>
            <span>Existing Digital Assets</span>
            <FieldInfo fieldKey="socialPlatforms" />
          </h3>
        </div>
        {/* <p className="text-xs text-gray-400 mb-4 ml-9">
          Add your social platform URLs — non-blocking, can be updated later
        </p> */}

        <div className="space-y-4">
          {props.socialPlatforms.map((social, index) => (
            <div key={index}>
              <label className="block text-sm text-gray-700 mb-1.5">
                {social.platform}
              </label>
              <input
                type="url"
                value={social.url}
                onChange={(e) =>
                  props.onUpdateSocialPlatform(index, e.target.value.slice(0, 60))
                }
                maxLength={60}
                placeholder={`https://${social.platform.toLowerCase().replace(/[^a-z]/g, "")}.com/yourbrand`}
                className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                  props.errors?.[`socialPlatforms_${index}_url`] ? 'border-red-400 bg-red-50' : 'border-gray-200'
                }`}
              />
              {props.errors?.[`socialPlatforms_${index}_url`] && (
                <p className="mt-1 text-xs text-red-500">{props.errors[`socialPlatforms_${index}_url`]}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {props.errors?.apiError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600 text-sm">
          <i className="ri-error-warning-line"></i>
          {props.errors.apiError}
        </div>
      )}

      {/* Logo Editor Modal */}
      {showEditor && editorImage && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-gray-900">Adjust Brand Logo</h3>
              <button
                onClick={() => setShowEditor(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="relative flex-1 min-h-[300px] flex items-center justify-center p-4 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+PHJlY3Qgd2lkdGg9IjIwIiBoZWlnaHQ9IjIwIiBmaWxsPSIjZmZmIi8+PHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjZjBmMGYwIi8+PHJlY3QgeD0iMTAiIHk9IjEwIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiNmMGYwZjAiLz48L3N2Zz4=')]">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                ruleOfThirds
              >
                <img 
                  ref={imgRef} 
                  src={editorImage} 
                  alt="Crop me" 
                  onLoad={onImageLoad}
                  style={{ maxHeight: '60vh', minWidth: '200px', width: '100%', objectFit: 'contain' }} 
                />
              </ReactCrop>
            </div>

            <div className="p-6 space-y-6 bg-white flex justify-end">
              <button
                onClick={() => setShowEditor(false)}
                className="px-6 py-2.5 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors mr-3 cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCrop}
                disabled={uploading}
                className="px-6 py-2.5 bg-teal-500 hover:bg-teal-600 text-white rounded-xl text-sm font-semibold shadow-lg shadow-teal-500/20 transition-all active:scale-95 disabled:opacity-50 cursor-pointer"
              >
                {uploading ? "Saving..." : "Save Crop"}
              </button>
            </div>
          </div>
        </div>
      )}
      {logoFile && logoPreview && (
        <LogoBackgroundRemovalModal
          open={showBgRemoval}
          originalUrl={logoPreview}
          removeBackground={() =>
            apiService.removeOnboardingLogoBackground(props.userId, logoFile).then((res) => res.data)
          }
          onUse={handleUseTransparentLogo}
          onClose={() => setShowBgRemoval(false)}
          brandColor={props.primaryColor}
          accent="teal"
        />
      )}

      {showPreviewModal && (logoPreview || props.brandLogo) && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm cursor-zoom-out"
          onClick={() => setShowPreviewModal(false)}
        >
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => setShowPreviewModal(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <i className="ri-close-line text-3xl"></i>
            </button>
            <img
              src={logoPreview || props.brandLogo || ""}
              alt="Brand Logo Preview"
              className="max-w-full max-h-[80vh] object-contain bg-white rounded-lg p-8 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Image Utilities for Cropping
 // Extracted crop utilities
 */
